import {useRef, useState} from 'react'
import {Box, IconButton, CircularProgress, Snackbar, Alert} from '@mui/material'
import {GalleryExport} from 'iconsax-reactjs'
import kc from 'web-common/services/auth'
import {v4 as UUIDv4} from 'uuid'
import FileStorage from 'web-common/services/files/FileStorage'
import {useTranslation} from 'react-i18next'

interface AttachImageButtonProps {
  disabled?: boolean
  onImageSelect: (fileBlob: string, filePath: string) => void
}

const AttachImageButton = ({
  disabled,
  onImageSelect
}: AttachImageButtonProps) => {
  const [t] = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleClick = () => {
    if (!loading) {
      fileInputRef.current?.click()
    }
  }

  const allowedFileTypes = ['image/jpeg', 'image/png', 'image/webp']

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!allowedFileTypes.includes(file.type)) {
      setErrorMessage(t('fnd-assistant-button-attach-image-error-mime'))
      return
    }

    setLoading(true)

    const reader = new FileReader()
    reader.readAsDataURL(file)

    reader.onloadend = async () => {
      const fileBlob = reader.result as string
      const userId = kc.instance.subject!
      const uploadPath = `user/${userId}/assistant/`
      const fileName = `${UUIDv4()}.${file.name.split('.').pop()}`

      try {
        const filePath = await FileStorage.uploadResource(
          file,
          uploadPath,
          fileName,
          true
        )
        onImageSelect(fileBlob, filePath)
      } catch (err) {
        setErrorMessage(t('fnd-assistant-button-attach-image-error-upload'))
      } finally {
        setLoading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }
  }

  const handleCloseSnackbar = () => {
    setErrorMessage(null)
  }

  return (
    <>
      <Box
        sx={{
          width: 'auto',
          marginX: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <input
          hidden
          ref={fileInputRef}
          type="file"
          accept={allowedFileTypes.join(', ')}
          onChange={handleFileChange}
        />
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled || loading}
          sx={(theme) => ({
            width: 40,
            height: 40,
            padding: 0,
            color: theme.palette.common.black
          })}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <GalleryExport size={24} />
          )}
        </IconButton>
      </Box>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      >
        <Alert severity="error" onClose={handleCloseSnackbar}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default AttachImageButton
